import { SaveOutlined } from '@ant-design/icons'
import { Button, message, Select, Typography } from 'antd'
import { RolodexDocumentEntry } from 'features/files/types'
import { useGeckoNotifications } from 'features/notifications'
import { useSageService } from 'features/sage'
import { Sparkles } from 'lucide-react'
import * as React from 'react'
import { useCallback, useEffect } from 'react'
import styled from 'styled-components'

const { Text } = Typography
const { Option } = Select

/**
 * Builds the categorization prompt for the LLM service
 */
const buildCategorisationPrompt = (
  documentName: string,
  categories: ReadonlyArray<string>
): string => {
  return `
You're an expert mechanical engineer and document categoriser with 
a deep understanding of mechanical engineering and factory-related documents.

What is the category of this document?

Document: ${documentName}
Possible Categories: ${categories.join(', ')}

Answer with only the category of the document and nothing else. If you don't know, choose 'Uncategorized'. Sample answer: 
U1        
`
}

/**
 * Props for document category selector component
 */
export interface DocumentCategorySelectorProps {
  categories: ReadonlyArray<string>
  onSave: (category: string | null) => Promise<void>
  document?: RolodexDocumentEntry
  saving?: boolean
}

const Container = styled.div`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  margin-bottom: 16px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`

const SaveButton = styled(Button)`
  font-weight: 600;
  height: 36px;
  border-radius: 6px;
`

const GenerateButton = styled(Button)`
  font-weight: 600;
  height: 36px;
  border-radius: 6px;
`

export const DocumentCategorySelector: React.FC<
  DocumentCategorySelectorProps
> = ({ categories, onSave, saving = false, document }) => {
  const { fetchVertexQuery } = useSageService()
  const [isInferring, setIsInferring] = React.useState(false)
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    document?.categories[0] ?? null
  )
  const { warningNotification } = useGeckoNotifications()

  const handleSave = useCallback(async () => {
    if (selectedCategory) {
      await onSave(selectedCategory)
    }
  }, [selectedCategory, onSave])

  const handleInferCategory = useCallback(
    async (autoSave = false) => {
      const rolodexFileRef = document?.rolodex_file_ref

      if (!rolodexFileRef) {
        message.error(
          `No file available for inference. Document: ${document?.filename}`
        )
        warningNotification('No file available for inference')

        return
      }

      setIsInferring(true)
      try {
        const apiResponse = await fetchVertexQuery({
          rolodexFiles: [
            {
              id: rolodexFileRef.entry_id,
              path: '/' + rolodexFileRef.path,
            },
          ],
          query: buildCategorisationPrompt(
            document.filename || 'Unknown Document',
            categories
          ),
        })

        const inferredCategory = apiResponse.result.trim()

        // Check if the inferred category matches one of our categories
        const matchedCategory = categories.find(
          (category) =>
            category.toLowerCase() === inferredCategory.toLowerCase()
        )

        if (matchedCategory) {
          setSelectedCategory(matchedCategory)

          // Auto-save if this was triggered automatically
          if (autoSave) {
            await onSave(matchedCategory)
          } else {
            message.success(
              `Successfully inferred category: ${matchedCategory}`
            )
          }
        } else {
          message.info(
            `Inferred category "${inferredCategory}" doesn't match available categories`
          )
        }
      } catch (error) {
        console.error('Error inferring category:', error)
        message.error('Failed to infer document category. Please try again.')
        setSelectedCategory('Uncategorized')
      } finally {
        setIsInferring(false)
      }
    },
    [
      document?.rolodex_file_ref,
      document?.filename,
      warningNotification,
      fetchVertexQuery,
      categories,
      onSave,
    ]
  )

  useEffect(() => {
    // When the document changes
    setSelectedCategory(document?.categories[0] ?? null)
    setIsInferring(false)

    // Auto-infer category if the document has no categories assigned
    if (
      document &&
      document.categories.length === 0 &&
      document.rolodex_file_ref
    ) {
      handleInferCategory(true)
    }
  }, [
    document,
    document?.categories,
    document?.rolodex_file_ref,
    handleInferCategory,
  ])

  return (
    <Container>
      <Text strong style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
        Document Category
      </Text>

      <Text type='secondary' style={{ marginBottom: 12, display: 'block' }}>
        Select a category for this document to help with organization and AI
        processing.
      </Text>

      <Select
        placeholder={
          isInferring ? 'Inferring category...' : 'Select a category'
        }
        value={selectedCategory || undefined}
        onChange={setSelectedCategory}
        style={{ width: '100%' }}
        disabled={isInferring}
        allowClear>
        {categories.map((category) => (
          <Option key={category} value={category}>
            {category}
          </Option>
        ))}
      </Select>

      <ButtonGroup>
        <GenerateButton
          type='default'
          onClick={() => handleInferCategory(false)}
          loading={isInferring}
          disabled={isInferring}
          icon={<Sparkles size={16} />}>
          Infer Category
        </GenerateButton>

        <SaveButton
          type='default'
          onClick={handleSave}
          loading={saving}
          disabled={!selectedCategory}
          icon={<SaveOutlined />}>
          Save Category
        </SaveButton>
      </ButtonGroup>
    </Container>
  )
}
